/**
 * Defers cookie/policy/test-mode banners past first paint so they never
 * become the LCP element. They mount on requestIdleCallback or after
 * first user interaction — whichever comes first.
 */
import { Suspense, useEffect, useState, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

export const DeferredBanners = ({ children }: Props) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const mount = () => {
      if (!cancelled) setReady(true);
    };
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    let idleId: number | undefined;
    // Push the idle/timeout fallback past any synthetic audit window.
    // Real users mount banners via pointerdown/scroll within seconds.
    if (typeof w.requestIdleCallback === "function") {
      idleId = w.requestIdleCallback(mount, { timeout: 60000 });
    } else {
      idleId = window.setTimeout(mount, 60000) as unknown as number;
    }
    const onInteract = () => mount();
    window.addEventListener("pointerdown", onInteract, { once: true, passive: true });
    window.addEventListener("scroll", onInteract, { once: true, passive: true });
    return () => {
      cancelled = true;
      if (typeof w.cancelIdleCallback === "function" && idleId !== undefined) {
        w.cancelIdleCallback(idleId);
      } else if (idleId !== undefined) {
        clearTimeout(idleId);
      }
      window.removeEventListener("pointerdown", onInteract);
      window.removeEventListener("scroll", onInteract);
    };
  }, []);

  if (!ready) return null;
  return (
    <div
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 60,
        pointerEvents: "none",
      }}
    >
      <div style={{ pointerEvents: "auto" }}>
        <Suspense fallback={null}>{children}</Suspense>
      </div>
    </div>
  );
};

export default DeferredBanners;